/**
 * Ministry of Justice — Official Letterhead PDF Export
 * Clean professional government letterhead style
 * Uses flow-based layout for reliable print output
 */

export const exportSubmissionPDF = (submission, formFields, formSettings, language = 'en', emailResponses = [], themeSettings = {}) => {
    const isRTL = language === 'ar'

    let fields = formFields
    if (typeof fields === 'string') {
        try { fields = JSON.parse(fields) } catch (e) { fields = [] }
    }

    const settings = typeof formSettings === 'string' ? JSON.parse(formSettings || '{}') : (formSettings || {})

    // Logos - Use logo from theme settings, fallback to default MoJ logo
    const logoUrl = themeSettings?.logoUrl || '/moj-logo.png'
    const watermarkLogoUrl = themeSettings?.logoUrl || '/moj-logo.png'

    // Check if QR code should be generated (based on form settings)
    // Print Settings - Warning, Note, and Footer
    const printWarning = isRTL ? (settings.print_warning_ar || '') : (settings.print_warning_en || '')
    const printNote = isRTL ? (settings.print_note_ar || '') : (settings.print_note_en || '')
    const printFooter = isRTL ? (settings.print_footer_ar || '') : (settings.print_footer_en || '')

    // MoJ Brand Colors
    const primaryColor = '#1a3a6b'

    // Contact Info
    const website = 'www.moj.gov.sy'
    const email = 'info@moj.gov.sy'
    const tel = '+963115400800'
    const fax = '+963115400700'

    // Helper to get option label from field definition
    const getOptionLabel = (field, value) => {
        if (!field || !field.options || !Array.isArray(field.options)) return null

        // Find the option that matches the value
        const option = field.options.find(opt => {
            // Handle both object format {value, label_en, label_ar} and simple string format
            if (typeof opt === 'object') {
                return opt.value === value || opt.id === value
            }
            return opt === value
        })

        if (!option) return null

        if (typeof option === 'object') {
            // Return Arabic or English label based on export language
            return isRTL ? (option.label_ar || option.label_en || option.label || option.value)
                : (option.label_en || option.label || option.value)
        }

        return option // Simple string option
    }

    const renderValue = (val, field = null) => {
        if (!val && val !== 0) return '—'
        if (typeof val === 'string' && val.includes('uploads/')) return val.split('/').pop()

        // For select/radio fields, try to get the translated option label
        if (field && (field.type === 'select' || field.type === 'radio')) {
            const optionLabel = getOptionLabel(field, val)
            if (optionLabel) return optionLabel
        }

        // For checkbox fields with multiple values
        if (field && field.type === 'checkbox' && Array.isArray(val)) {
            const labels = val.map(v => {
                const optionLabel = getOptionLabel(field, v)
                return optionLabel || v
            })
            return labels.join('، ') // Arabic comma
        }

        if (Array.isArray(val)) return val.length ? val.map(v => typeof v === 'object' ? Object.values(v).join(', ') : v).join('، ') : '—'
        if (typeof val === 'object') return Object.values(val).join(', ')
        return String(val)
    }

    // Filter fields based on export settings
    const exportAllFields = settings.export_all_fields !== false
    const exportFieldIds = settings.export_fields || []
    const fieldsToExport = exportAllFields
        ? fields
        : fields.filter(f => exportFieldIds.includes(f.id))

    // Column layout setting (1, 2, or 3 columns)
    const columnCount = parseInt(settings.export_columns) || 1

    // Manual column assignments (fieldId -> columnNumber)
    const fieldColumnAssignments = settings.export_field_columns || {}

    // Debug logging
    console.log('PDF Export Column Settings:', {
        export_columns: settings.export_columns,
        columnCount,
        export_field_columns: settings.export_field_columns,
        fieldColumnAssignments,
        export_all_fields: settings.export_all_fields
    })

    // Build field data array with column assignments (using filtered fields)
    const dataItems = fieldsToExport.map(f => {
        const val = submission.data?.[f.id]
        if (val === undefined || val === '' || val === null) return null
        const label = isRTL && f.label_ar ? f.label_ar : (f.label_en || f.label || f.id)
        // Use manual column assignment if available, default to column 1
        const assignedColumn = fieldColumnAssignments[f.id] || 1
        return { label, value: renderValue(val, f), fieldId: f.id, column: assignedColumn }
    }).filter(Boolean)

    // Group data items by their assigned column
    const groupByColumn = (items, cols) => {
        const result = []
        for (let i = 0; i < cols; i++) {
            result.push([])
        }

        // If we have manual assignments (non-empty), use them
        const hasManualAssignments = Object.keys(fieldColumnAssignments).length > 0

        if (hasManualAssignments && cols > 1) {
            // Use manual column assignments
            items.forEach(item => {
                const colIndex = Math.min(Math.max(0, (item.column || 1) - 1), cols - 1)
                result[colIndex].push(item)
            })
        } else {
            // Auto-split evenly (fallback for Export All Fields or no assignments)
            const itemsPerCol = Math.ceil(items.length / cols)
            for (let i = 0; i < cols; i++) {
                result[i] = items.slice(i * itemsPerCol, (i + 1) * itemsPerCol)
            }
        }

        return result
    }

    const columnData = groupByColumn(dataItems, columnCount)

    // Generate column HTML
    const generateColumnHtml = (items) => {
        const rows = items.map(item => `
            <tr>
                <td class="lbl">${item.label}</td>
                <td class="val">${item.value}</td>
            </tr>
        `).join('')
        return `<table class="data-table"><tbody>${rows}</tbody></table>`
    }

    // Generate content based on column count
    const generateContentHtml = () => {
        if (columnCount === 1) {
            return generateColumnHtml(dataItems)
        } else {
            const colWidth = columnCount === 2 ? '48%' : '31%'
            const columnsHtml = columnData.map(items => `
                <div class="content-column" style="width: ${colWidth};">
                    <div class="content-card">
                        ${generateColumnHtml(items)}
                    </div>
                </div>
            `).join('')
            return `<div class="columns-grid">${columnsHtml}</div>`
        }
    }

    // Check if QR code should be generated (based on form settings OR direct submission prop)
    const formConfigQR = settings.generate_qr_code === true || settings.generate_qr_code === 'true' || settings.generate_qr_code === 1 || settings.generate_qr_code === "1"
    const submissionConfigQR = submission.form_generate_qr_code === 1 || submission.form_generate_qr_code === true || submission.form_generate_qr_code === '1'
    const generateQR = formConfigQR || submissionConfigQR

    // QR code URL - if form has generateQR enabled, use verification_code or fallback to submission_id
    let qrUrl = null
    if (generateQR) {
        const qrContent = submission.verification_code || submission.submission_id
        if (qrContent) {
            qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrContent)}`
        }
    }

    // QR Section HTML - show for any submission with verification code (approved submissions)
    // Now also handles direct submissions via submission_id fallback
    // No verification code text here - it's already in doc-number-wrapper at the top
    const qrSectionHtml = qrUrl ? `
        <div class="qr-section">
            <div class="qr-frame">
                <img class="qr-image" src="${qrUrl}" alt="QR">
            </div>  
            <div class="qr-note">يرجى مسح هذا الرمز عند المدخل</div>    
        </div>
    ` : ''

    const htmlContent = `<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
    <meta charset="UTF-8">
    <title>${submission.submission_id}</title>
    <style>
        @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');
        
        * { margin: 0; padding: 0; box-sizing: border-box; }
        
        @page { 
            size: A4; 
            margin: 0;
        }
        
        body {
            font-family: 'Cairo', sans-serif;
            font-size: 11pt;
            line-height: 1.6;
            color: #333;
            background: #fff;
            direction: rtl;
            padding-top: 140px;
            padding-bottom: 100px;
            padding-left: 10mm;
            padding-right: 10mm;
        }

        /* ==================== WATERMARK ==================== */
        .watermark {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            opacity: 0.06;
            z-index: 0;
            pointer-events: none;
        }
        .watermark img {
            width: 500px;
            height: auto;
        }

        /* ==================== HEADER (fixed on every page) ==================== */
        .header {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            padding: 15px 30px 10px 30px;
            border-bottom: 2px solid ${primaryColor};
            background: #fff;
            z-index: 10;
        }

        .header-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .header-left {
            text-align: center;
            flex: 1;
        }
        .header-left .country-en {
            font-size: 16px;
            font-weight: 600;
            color: #333;
        }

        .header-center {
            text-align: center;
            flex: 1;
        }
        .header-center .logo {
            height: 100px;
        }

        .header-right {
            text-align: center;
            flex: 1;
        }
        .header-right .country-ar {
            font-size: 16px;
            font-weight: 600;
            color: #333;
        }

        /* ==================== DOCUMENT NUMBER BOX ==================== */
        .doc-number-box {
            text-align: center;
            margin: 15px 30px;
            padding: 12px 30px;
            background: #e8f2ef;
            border: 2px solid ${primaryColor};
            border-radius: 15px;
        }
        .doc-number-wrapper {
            text-align: center;
            margin-bottom: 25px;
        }
        .doc-number-box span {
            font-size: 13pt;
            font-weight: 700;
            color: #151415;
            letter-spacing: 1px;
        }

        /* ==================== MAIN CONTENT ==================== */
        .main-content {
            flex: 1;
            padding: 0 30px;
            padding-bottom: 40px;
        }

        /* ==================== DATA TABLE ==================== */
        .data-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
        }
        .data-table tr {
            border-bottom: 1px solid #e0e0e0;
        }
        .data-table tr:last-child {
            border-bottom: none;
        }
        .data-table td {
            padding: 10px 12px;
            vertical-align: top;
        }
        .data-table .lbl {
            font-weight: 600;
            color: #555;
            text-align: right;
            width: 35%;
        }
        .data-table .val {
            text-align: right;
            width: 65%;
            color: #000;
        }

        /* ==================== MULTI-COLUMN LAYOUT ==================== */
        .columns-grid {
            display: flex;
            flex-direction: row-reverse;
            gap: 15px;
            justify-content: space-between;
        }
        .content-column {
            flex: 1;
        }
        .content-card {
            border: 2px solid #1a3a6b;
            border-radius: 8px;
            padding: 10px;
            background: #e8f2ef3b;
        }
        .content-card .data-table {
            margin-bottom: 0;
        }
        .content-card .data-table .lbl {
            width: 45%;
        }
        .content-card .data-table .val {
            width: 55%;
        }

        /* ==================== QR SECTION ==================== */
        .qr-section {
            text-align: center;
            padding:  0;
            margin-top: 30px;
        }

        .qr-frame {
            display: inline-block;
            padding: 15px;
            background: #fff;
            border: 2px solid #ddd;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .qr-image {
            width: 150px;
            height: 150px;
            display: block;
        }
        .qr-note {
            margin-top: 12px;
            font-size: 9pt;
            color: #666;
        }

        /* ==================== ALERT BOXES ==================== */
        .alert-box {
            padding: 12px 15px;
            border-radius: 6px;
            margin: 10px 0;
            font-size: 9pt;
        }
        .alert-warning {
            background: #fef3cd;
            border: 1px solid #d4a106;
            color: #856404;
        }
        .alert-note {
            background: #e8f4fd;
            border: 1px solid #2196f3;
            color: #0d47a1;
        }
        .alert-icon {
            margin-left: 8px;
            font-size: 12pt;
        }

        /* ==================== FOOTER (fixed on every page) ==================== */
        .footer {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            background: #fff;
            border-top: 1px solid ${primaryColor};
            padding-top: 10px;
            z-index: 10;
        }

        .footer-content {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 30px;
            font-size: 8pt;
            color: #555;
        }

        .footer-left { text-align: left; line-height: 1.4; }
        .footer-center { text-align: center; line-height: 1.4; }
        .footer-right { text-align: right; line-height: 1.4; }

        .footer-bar {
            height: 8px;
            background: ${primaryColor};
            margin-top: 8px;
        }

        .custom-footer-text {
            text-align: center;
            padding: 6px 30px;
            font-size: 8pt;
            color: #666;
            font-style: italic;
        }

        /* ==================== RESPONSE SECTION ==================== */
        .response-page {
            page-break-before: always;
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: calc(100vh - 240px);
        }
        .response-page-inner {
            width: 100%;
        }
        .response-section {
            margin: 15px 0;
            border: 2px solid ${primaryColor};
            border-radius: 6px;
            overflow: hidden;
        }
        .response-header {
            background: ${primaryColor};
            color: #fff;
            padding: 8px 15px;
            font-weight: bold;
            font-size: 10pt;
            text-align: center;
        }
        .response-body {
            padding: 12px 15px;
        }
        .response-subject {
            font-weight: bold;
            font-size: 9pt;
            margin-bottom: 6px;
            color: ${primaryColor};
        }
        .response-message {
            font-size: 9pt;
            line-height: 1.6;
            white-space: pre-wrap;
        }
        .response-meta {
            margin-top: 8px;
            font-size: 7.5pt;
            color: #888;
            text-align: ${isRTL ? 'right' : 'left'};
        }

        /* ==================== PRINT STYLES ==================== */
        @media print {
            body { 
                -webkit-print-color-adjust: exact !important; 
                print-color-adjust: exact !important; 
            }
            .watermark { position: fixed; }
            .header {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
            }
            .footer {
                position: fixed;
                bottom: 0;
                left: 0;
                right: 0;
            }
        }
    </style>
</head>
<body>

<!-- Watermark -->
<div class="watermark">
    <img src="${watermarkLogoUrl}" alt="">
</div>

<!-- Header (fixed, repeats on every page) -->
<div class="header">
    <div class="header-row">
        <div class="header-left">
            <div class="country-en">Syrian Arab Republic</div>
        </div>
        <div class="header-center">
            <img class="logo" src="${logoUrl}" alt="Ministry of Justice">
        </div>
        <div class="header-right">
            <div class="country-ar">الجمهورية العربية السورية</div>
        </div>
    </div>
</div>

<!-- Footer (fixed, repeats on every page) -->
<div class="footer">
    ${printFooter ? `
    <div class="custom-footer-text">
        ${printFooter}
    </div>
    ` : ''}
    <div class="footer-content">
        <div class="footer-left">
            Tel: ${tel}<br>
            Fax: ${fax}
        </div>
        <div class="footer-center">
            ${website}<br>
            ${email}
        </div>
        <div class="footer-right">
            دمشق، سورية<br>
            Syria Damascus
        </div>
    </div>
    <div class="footer-bar"></div>
</div>

<!-- Document Number -->
<div class="doc-number-wrapper">
    <div class="doc-number-box">
        <span>${submission.submission_id}</span>
    </div>
</div>

<!-- Main Content -->
<div class="main-content">
    ${generateContentHtml()}

    ${(settings.export_include_response && emailResponses && emailResponses.length > 0) ? (() => {
            const resp = emailResponses[0]
            const subject = isRTL ? (resp.subject_ar || resp.subject_en || '') : (resp.subject_en || resp.subject_ar || '')
            const message = isRTL ? (resp.message_ar || resp.message_en || '') : (resp.message_en || resp.message_ar || '')
            const responder = isRTL ? (resp.user_name_ar || resp.user_name_en || '') : (resp.user_name_en || resp.user_name_ar || '')
            const sentDate = resp.sent_at ? new Date(resp.sent_at).toLocaleDateString(isRTL ? 'ar-SY' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''
            return `
    </div><!-- close main-content for page break -->
    <div class="response-page">
        <div class="response-page-inner">
            <div class="response-section">
                <div class="response-header">${isRTL ? 'الرد الرسمي' : 'Official Response'}</div>
                <div class="response-body" dir="${isRTL ? 'rtl' : 'ltr'}">
                    ${subject ? `<div class="response-subject">${isRTL ? 'الموضوع:' : 'Subject:'} ${subject}</div>` : ''}
                    <div class="response-message">${message}</div>
                    <div class="response-meta">
                        ${responder ? `${isRTL ? 'بواسطة:' : 'By:'} ${responder}` : ''}
                        ${sentDate ? ` — ${sentDate}` : ''}
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="main-content" style="padding-top:0;">`
        })() : ''}

    ${qrSectionHtml}

    ${printWarning ? `
    <div class="alert-box alert-warning">
        <span class="alert-icon">⚠️</span>
        ${printWarning}
    </div>
    ` : ''}

    ${printNote ? `
    <div class="alert-box alert-note">
        <span class="alert-icon">ℹ️</span>
        ${printNote}
    </div>
    ` : ''}
</div>

</body>
</html>`

    const printWindow = window.open('', '_blank', 'width=900,height=1100')
    if (!printWindow) { alert('يرجى السماح بالنوافذ المنبثقة'); return }
    printWindow.document.write(htmlContent)
    printWindow.document.close()
    printWindow.onload = () => setTimeout(() => { printWindow.focus(); printWindow.print() }, 500)
}

export default exportSubmissionPDF
